import PropTypes from 'prop-types';
import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  DEFAULT_DATA_REGION,
  DATA_REGION_STORAGE_KEY,
  isValidDataRegion,
} from './regions';

const ProjectContext = createContext();

export const useProject = () => useContext(ProjectContext);

/**
 * Validates if a stored project object is valid and has all required fields
 * @param {any} project - The project object to validate
 * @returns {boolean} - True if project is valid, false otherwise
 */
const isValidProject = (project) => {
  return (
    project &&
    typeof project === 'object' &&
    project.code &&
    project.id &&
    Array.isArray(project.role)
  );
};

export const ProjectProvider = ({ children }) => {
  const [project, setProject] = useState(() => {
    const storedProject = localStorage.getItem('project');
    try {
      const parsedProject = storedProject ? JSON.parse(storedProject) : null;
      // Only return the stored project if it's valid (has required fields)
      if (isValidProject(parsedProject)) {
        return parsedProject;
      }
    } catch (error) {
      console.error('Error parsing stored project:', error);
    }
    // Return empty default project if stored project is invalid or missing
    return {
      code: '',
      name: '',
      devicePermissions: [],
      role: [],
      id: '',
    };
  });

  const [selectedProjects, setSelectedProjects] = useState(() => {
    const stored = localStorage.getItem('selectedProjects');
    try {
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error parsing stored selected projects:', error);
      return [];
    }
  });

  // Selected data region (feature 021-china-account-access). Defaults to Global.
  const [region, setRegion] = useState(() => {
    const stored = localStorage.getItem(DATA_REGION_STORAGE_KEY);
    return isValidDataRegion(stored) ? stored : DEFAULT_DATA_REGION;
  });

  // Wrapper function to sync project changes with localStorage
  const setProjectAndPersist = useCallback((newProject) => {
    setProject(newProject);
    if (newProject && newProject.code && newProject.id) {
      localStorage.setItem('project', JSON.stringify(newProject));
    }
  }, []);

  // Wrapper function to sync selectedProjects changes with localStorage
  const setSelectedProjectsAndPersist = useCallback((newSelectedProjects) => {
    setSelectedProjects(newSelectedProjects);
    if (newSelectedProjects) {
      localStorage.setItem('selectedProjects', JSON.stringify(newSelectedProjects));
    }
  }, []);

  // Wrapper to change the data region. Only valid regions are accepted and the
  // selection is persisted so backend requests (and reloads) stay consistent.
  const setRegionAndPersist = useCallback((newRegion) => {
    const safeRegion = isValidDataRegion(newRegion) ? newRegion : DEFAULT_DATA_REGION;
    setRegion(safeRegion);
    localStorage.setItem(DATA_REGION_STORAGE_KEY, safeRegion);
  }, []);

  /**
   * Attempt to restore the last used project from available projects
   * Returns the last project if it exists in the available projects list
   * Falls back to the first available project if stored project is not found
   * @param {Array} availableProjects - List of projects the user has access to
   * @param {boolean} setDefaultIfNotFound - If true, returns first project when stored project not found
   * @returns {Object|null} - The restored project, default project, or null if not found
   */
  const restoreLastUsedProject = useCallback((availableProjects, setDefaultIfNotFound = false) => {
    if (!availableProjects || availableProjects.length === 0) {
      return null;
    }

    const storedProject = localStorage.getItem('project');
    
    try {
      // If there's a stored project, try to match it
      if (storedProject) {
        const parsedStoredProject = JSON.parse(storedProject);
        
        // Find if the stored project exists in available projects
        const restoredProject = availableProjects.find(
          (p) => p.id === parsedStoredProject.id || p.code === parsedStoredProject.code
        );

        if (restoredProject) {
          return restoredProject;
        }
      }
      
      // If stored project not found and setDefaultIfNotFound is true, use first available project
      if (setDefaultIfNotFound && availableProjects.length > 0) {
        console.log('Stored project not found or invalid. Setting first available project as default.');
        return availableProjects[0];
      }

      return null;
    } catch (error) {
      console.error('Error restoring last used project:', error);
      
      // On error, if setDefaultIfNotFound is true, return first available project
      if (setDefaultIfNotFound && availableProjects.length > 0) {
        console.log('Error parsing stored project. Setting first available project as default.');
        return availableProjects[0];
      }
      
      return null;
    }
  }, []);

  const value = { 
    project, 
    setProject: setProjectAndPersist, 
    selectedProjects, 
    setSelectedProjects: setSelectedProjectsAndPersist,
    restoreLastUsedProject,
    isValidProject,
    region,
    setRegion: setRegionAndPersist,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

ProjectProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
